export const accountEmail = 'accounts@jumpcloud.com';

export const TrialLinks = {
  fairUsePolicy: {
    url: 'https://jumpcloud.com/fair-use-policy',
    text: 'fair use policy',
  },
  account: {
    url: '#/account/general',
    text: 'See More.',
  },
  pricing: {
    externalLink: 'https://jumpcloud.com/pricing',
    text: 'See More.',
  },
  salesContact: {
    url: `mailto:${accountEmail}`,
    text: 'Reach out to us',
  },
  stopATrial: {
    url:
      'https://jumpcloud-support.force.com/support/s/article/Start-or-Stop-a-Trial',
    text: 'Stop a Trial',
  },
};

export const FeatureTrialTextConsts = {
  zeroTrust: {
    description:
      'Enforce conditional MFA for multiple users or devices by applying a conditional access policy. Implement a Zero Trust security model to secure user access to devices, applications, files, networks, and other resources from a single cloud directory platform.',
    featureTrialEndingInstruction:
      'To end your trial of Conditional Access Policies, delete or disable any policies using conditions, or remove conditions from those policies.',
    includedFeatures: ['Device Trust', 'Network Trust', 'Geolocation Trust', 'Disk Encryption'],
  },

  osPatchManagement: {
    description:
      'JumpCloud Patch Management enables you to easily create schedules and manage all your macOS, Windows, Linux (Ubuntu), and Chrome browser patches in a single console for greater visibility, automation, and reporting.',
    featureTrialEndingInstruction:
      'To end your trial of Patch Management, delete all of your Patch Management policies.',
    includedFeatures: [
      'Mac OS Patching',
      'Linux Ubuntu OS Patching',
      'Windows OS Patching',
      'Chrome Browser Patching',
    ],
  },

  passwordManagement: {
    description:
      'The JumpCloud Password Manager ™ enables your team to securely manage and share passwords, utilize two-factor authentication (2FA), and other types of sensitive information while providing full visibility and control over passwords used across your organization. Administrators now have centralized management control to spot weak or reused passwords and visualize usage logs all in one place.',
    featureTrialEndingInstruction:
      'To end your trial of Password Manager, remove all of your enrolled user groups.',
    includedFeatures: [
      'Decentralized storage architecture',
      'Auto-fill passwords and 2FA',
      'Securely share passwords and 2FA',
      'Generate strong passwords',
      'Administrative controls and reporting',
      'Cloud Backups',
    ],
  },
};

const PasswordManagerTrialConfig = {
  code: 'passwordManagement',
  name: 'Password Manager',
  package: 'Password Manager',
  productCode: 'PASSWORD-MGMT',
  eventLocation: 'Password Manager Trial Modal',
  texts: FeatureTrialTextConsts.passwordManagement,
  isAddOn: true,
};

const OSPatchManagementTrialConfig = {
  code: 'osPatchManagement',
  name: 'Patch Management',
  package: 'Patch Management',
  productCode: 'PATCH-2021',
  eventLocation: 'Patch Management Trial Modal',
  texts: FeatureTrialTextConsts.osPatchManagement,
  isAddOn: true,
};

const ZeroTrustTrialConfig = {
  code: 'zeroTrust',
  name: 'Conditional Access Policies',
  package: 'Platform Plus',
  eventLocation: 'Conditional Policies Trial Modal',
  productCode: 'PLATFORM-PLUS-2021',
  texts: FeatureTrialTextConsts.zeroTrust,
  featurePackageMarkdown: 'Platform*Plus*',
  recapDisclaimer: 'The number of Total Policies reflected above may be lower than expected if individual policies carry more than one condition. Policies with multiple conditions are counted as one policy.',
};

export const TrialFeatures = {
  [PasswordManagerTrialConfig.code]: PasswordManagerTrialConfig,
  [OSPatchManagementTrialConfig.code]: OSPatchManagementTrialConfig,
  [ZeroTrustTrialConfig.code]: ZeroTrustTrialConfig,
};

export const UpgradePageCardsData = [
  {
    title: 'Password Manager',
    planDescription:
      'The JumpCloud Password Manager™ enables your team to securely manage and share passwords, two-factor authentication (2FA), and other types of sensitive information while providing you with full visibility and control over passwords used across your organization.',
    rateType: 'user',
    code: TrialFeatures.passwordManagement.code,
    upgradeType: 'Add On',
    planComponents: [
      {
        name: '',
        features: [
          'Decentralized storage architecture',
          'Auto-fill passwords and 2FA',
          'Securely share passwords and 2FA',
        ],
      },
      {
        name: '',
        features: [
          'Generate strong passwords',
          'Administrative controls and reporting',
          'Cloud Backups',
        ],
      },
    ],
    order: 1,
    badgeTextBase: 'You have been trying Password Manager for',
    productCode: TrialFeatures.passwordManagement.productCode,
    featureLink: 'passwordManager',
  },

  {
    title: TrialFeatures.osPatchManagement.package,
    code: TrialFeatures.osPatchManagement.code,
    productCode: TrialFeatures.osPatchManagement.productCode,
    planDescription:
      'JumpCloud’s automated patch management helps you secure your fleet to remotely manage, schedule, install OS and browser updates for your macOS, Windows, and Ubuntu devices.',
    rateType: 'user',
    upgradeType: 'Add On',
    upgradeTypeText:
      'Explore features individually with Add Ons. Find more on our ',
    planComponents: [
      {
        name: 'OS Patching',
        features: ['Mac', 'Linux Ubuntu', 'Windows'],
      },
      {
        name: 'Browser Patching',
        features: ['Chrome'],
      },
      {
        name: 'Coming Soon',
        features: ['Third-party Application Patching', 'Edge', 'Firefox'],
      },
    ],
    order: 2,
    badgeTextBase: 'You have been trying Patch Management for',
    featureLink: 'configurationsMainPanelOsPatchMgmt',
  },

  {
    title: TrialFeatures.zeroTrust.package,
    productCode: TrialFeatures.zeroTrust.productCode,
    planDescription:
      'Implement a Zero Trust security model to secure user access to devices, applications, files, networks, and other resources from a single cloud directory platform.',
    rateType: 'user',
    code: TrialFeatures.zeroTrust.code,
    includedFeatureText: 'Everything in Platform, plus:',
    upgradeType: 'Package',
    upgradeTypeText: 'To learn more about other plans see our ',
    upgradeFeatureText: 'Everything in Platform, plus:',
    planComponents: [
      {
        name: 'Zero Trust',
        features: [
          'Conditional Access Policies',
          'Device Trust',
          'Network Trust',
        ],
      },
      {
        name: 'Premium Support',
        features: [
          '24x7 support & implementation support with guaranteed uptime SLA',
        ],
      },
    ],
    order: 3,
    badgeTextBase: 'You have been trying Conditional Access Policies for',
    featureLink: 'authNPoliciesMainPanel',
  },
];

export const ContactModalBody = 'Our Annual and A La Carte plans are customizable. Get in touch with one of our account managers for help with your purchase today.';

export const ContactPhoneNumbers = [
  'USA: 1-855-212-3122',
  'UK: +44.20.4571.9942',
];

export const FeatureIllustrationUrl = 'https://s3.amazonaws.com/static.jumpcloud.com/adminportal/trial';
